<!--
  列配置工具组件
  功能：提供工具栏小图标（列设置/刷新/全屏），主要实现对 el-table 列的配置：
    - 拖拽调整列顺序
    - 是否显示（visible）
    - 是否可排序（sortable）
    - 溢出显示...（tooltip）
    - 冻结（fixed: 左侧/右侧/取消）
  配置结果存储到后端（userHabits），初始化时从后端读取并应用。
  实现方式参考 ViewTable.vue 的列设置/排序设置与 VmTable.vue 的 el-table store 操作。
  说明：不改动 el-table 已有模板，通过操作 el-table 内部 store._columns 实现。
-->
<template>
  <div class="column-config-tool">
    <div class="tool-icons">
      <div title="列设置" @click="openDialog">
        <i class="el-icon-menu"/>
      </div>
      <div title="刷新" @click="$emit('refresh')">
        <i class="el-icon-refresh"/>
      </div>
      <div :title="isFullscreen ? '还原' : '全屏'" @click="toggleFullscreen">
        <i :class="isFullscreen ? 'el-icon-aim' : 'el-icon-full-screen'"/>
      </div>
    </div>
    <el-dialog
        v-el-drag-dialog
        append-to-body
        title="列设置"
        :visible.sync="dialogVisible"
        width="900px"
        @closed="destroySortable">
      <!-- 表头说明行 -->
      <div class="config-header">
        <span class="config-col-name">列名</span>
        <span class="config-col-visible">是否显示</span>
        <span class="config-col-sortable">是否可排序</span>
        <span class="config-col-tooltip">溢出显示...</span>
        <span class="config-col-fixed">冻结</span>
      </div>
      <!-- 配置项 -->
      <div ref="sortList" class="config-body">
        <div v-for="item in dialogColumns" :key="item.label" class="config-row">
          <span class="config-col-name">
            <i class="el-icon-rank"/>
            <span>{{item.label}}</span>
          </span>
          <el-switch
              v-model="item.visible"
              class="config-col-visible"
              active-text="开启"
              inactive-text="关闭"/>
          <el-switch
              v-model="item.sortable"
              class="config-col-sortable"
              active-text="开启"
              inactive-text="关闭"/>
          <el-switch
              v-model="item.tooltip"
              class="config-col-tooltip"
              active-text="开启"
              inactive-text="关闭"/>
          <el-radio-group v-model="item.fixed" class="config-col-fixed fixed-radio">
            <el-radio label="left">左侧</el-radio>
            <el-radio label="right">右侧</el-radio>
            <el-radio label="">取消</el-radio>
          </el-radio-group>
        </div>
        <div v-if="!dialogColumns.length" class="config-empty">暂无可配置的列</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resetDefault">恢复默认</el-button>
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSave">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  import Sortable from "sortablejs";

  export default {
    name: "ColumnConfigTool",
    props: {
      // el-table 组件实例（由父组件在 mounted 后通过 $refs 传入）
      elTable: {
        type: Object,
        default: null,
      },
      // 后端存储标识（userHabits 的 code）
      code: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        dialogVisible: false,
        isFullscreen: false,
        // 头部固定列引用（序号、selection、fixed=left），不参与配置
        leadingColumnRefs: [],
        // 尾部固定列引用（操作列等 fixed=right 且无 property），不参与配置
        trailingColumnRefs: [],
        // 可配置的列引用（数据列），在初始化时捕获
        reorderableColumnRefs: [],
        // 默认配置（首次初始化时从列属性读取，用于恢复默认）
        defaultConfig: [],
        // 当前已应用的配置（含顺序与各项属性）
        savedConfig: [],
        // 弹窗中编辑的配置副本
        dialogColumns: [],
        sortableInstance: null,
      };
    },
    watch: {
      elTable: {
        handler(val) {
          if (val) {
            this.$nextTick(() => this.initColumns());
          }
        },
        immediate: true,
      },
    },
    beforeDestroy() {
      this.destroySortable();
    },
    methods: {
      // 初始化：读取列、分类、加载后端配置
      initColumns() {
        const columns = this.getTableColumns();
        if (!columns || !columns.length) return;
        this.categorizeColumns(columns);
        this.defaultConfig = this.buildConfigFromColumns(this.reorderableColumnRefs);
        this.loadConfig();
      },

      getTableColumns() {
        if (!this.elTable || !this.elTable.store) return [];
        return this.elTable.store.states._columns || [];
      },

      // 将列分为 头部固定 / 可配置 / 尾部固定
      categorizeColumns(columns) {
        this.leadingColumnRefs = [];
        this.trailingColumnRefs = [];
        this.reorderableColumnRefs = [];
        columns.forEach((col) => {
          if (this.isLeading(col)) {
            this.leadingColumnRefs.push(col);
          } else if (this.isTrailing(col)) {
            this.trailingColumnRefs.push(col);
          } else if (col.label) {
            this.reorderableColumnRefs.push(col);
          }
        });
      },

      isLeading(col) {
        return col.type === "index" || col.type === "selection" || col.fixed === "left" || col.fixed === true;
      },

      isTrailing(col) {
        // 操作列：fixed=right 且无 property（slot 渲染）
        return col.fixed === "right" && !col.property;
      },

      // 从列对象读取当前属性，生成配置项
      buildConfigFromColumns(columns) {
        return columns.map((col) => ({
          label: col.label,
          visible: true,
          sortable: !!col.sortable,
          tooltip: !!col.showOverflowTooltip,
          fixed: this.normalizeFixed(col.fixed),
        }));
      },

      normalizeFixed(fixed) {
        if (fixed === true || fixed === "left") return "left";
        if (fixed === "right") return "right";
        return "";
      },

      denormalizeFixed(fixed) {
        if (fixed === "left") return "left";
        if (fixed === "right") return "right";
        return false;
      },

      // 从后端加载已保存的配置
      loadConfig() {
        this.$http.get("basic", `userHabits?code=${this.code}`).then((res) => {
          if (res.data && res.data.description) {
            try {
              const saved = JSON.parse(res.data.description);
              this.savedConfig = this.mergeConfig(this.defaultConfig, saved);
            } catch (e) {
              this.savedConfig = this.defaultConfig.map((c) => ({ ...c }));
            }
          } else {
            this.savedConfig = this.defaultConfig.map((c) => ({ ...c }));
          }
          this.applyConfig();
        });
      },

      // 合并保存的配置与默认配置：保存的在前（保留顺序），新增列追加到末尾
      mergeConfig(defaultConfig, savedConfig) {
        if (!Array.isArray(savedConfig)) return defaultConfig.map((c) => ({ ...c }));
        const defaultLabels = new Set(defaultConfig.map((item) => item.label));
        const usedLabels = new Set();
        const result = [];
        savedConfig.forEach((saved) => {
          if (saved.label && defaultLabels.has(saved.label) && !usedLabels.has(saved.label)) {
            result.push({ ...saved });
            usedLabels.add(saved.label);
          }
        });
        defaultConfig.forEach((item) => {
          if (!usedLabels.has(item.label)) {
            result.push({ ...item });
          }
        });
        return result;
      },

      // 将 savedConfig 应用到 el-table
      applyConfig() {
        if (!this.elTable || !this.elTable.store) return;
        const colMap = {};
        this.reorderableColumnRefs.forEach((col) => {
          colMap[col.label] = col;
        });
        const visibleColumns = [];
        this.savedConfig.forEach((item) => {
          const col = colMap[item.label];
          if (!col) return;
          // 应用属性
          col.sortable = item.sortable ? true : false;
          col.showOverflowTooltip = !!item.tooltip;
          col.fixed = this.denormalizeFixed(item.fixed);
          if (item.visible) {
            visibleColumns.push(col);
          }
        });
        // 重建 _columns：头部固定 + 可见可配置列 + 尾部固定
        const newColumns = [...this.leadingColumnRefs, ...visibleColumns, ...this.trailingColumnRefs];
        this.elTable.store.states._columns = newColumns;
        this.elTable.store.updateColumns();
        if (typeof this.elTable.doLayout === "function") {
          this.elTable.doLayout();
        }
      },

      // 打开列设置弹窗
      openDialog() {
        if (!this.elTable || !this.elTable.store) {
          this.$message.warning("表格未就绪");
          return;
        }
        // 从已应用的配置构建可编辑副本
        this.dialogColumns = this.savedConfig.map((item) => ({ ...item }));
        this.dialogVisible = true;
        this.$nextTick(() => this.initSortable());
      },

      initSortable() {
        this.destroySortable();
        const el = this.$refs.sortList;
        if (!el) return;
        this.sortableInstance = Sortable.create(el, {
          animation: 150,
          onEnd: (evt) => {
            const arr = this.dialogColumns;
            const [moved] = arr.splice(evt.oldIndex, 1);
            arr.splice(evt.newIndex, 0, moved);
          },
        });
      },

      destroySortable() {
        if (this.sortableInstance) {
          this.sortableInstance.destroy();
          this.sortableInstance = null;
        }
      },

      // 保存到后端并应用
      handleSave() {
        this.savedConfig = this.dialogColumns.map((item) => ({ ...item }));
        const params = {
          code: this.code,
          description: JSON.stringify(this.savedConfig),
        };
        this.$http.post("basic", "userHabits", params).then((res) => {
          if (res.success) {
            this.$message.success("设置成功");
            this.applyConfig();
            this.dialogVisible = false;
          }
        });
      },

      // 恢复默认配置
      resetDefault() {
        this.dialogColumns = this.defaultConfig.map((item) => ({ ...item }));
      },

      // 全屏切换
      toggleFullscreen() {
        const fullscreenEnabled = document.fullscreenEnabled || document.webkitFullscreenEnabled;
        if (!fullscreenEnabled) return;
        const element = document.getElementsByClassName("vm-main-page")[0];
        if (!element) return;
        if (this.isFullscreen) {
          if (document.exitFullscreen) document.exitFullscreen();
          else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        } else {
          if (element.requestFullscreen) element.requestFullscreen();
          else if (element.webkitRequestFullScreen) element.webkitRequestFullScreen();
        }
        this.isFullscreen = !this.isFullscreen;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .column-config-tool {
    display: inline-flex;
  }
  .tool-icons {
    display: flex;
    div {
      width: 28px;
      height: 28px;
      border-radius: 15px;
      border: 1px solid #e8e8e8;
      line-height: 28px;
      text-align: center;
      font-size: 16px;
      cursor: pointer;
      margin-left: 8px;
      color: #606266;
      &:hover {
        color: #2d8cf0;
        border-color: #2d8cf0;
      }
    }
  }
  .config-header {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    font-weight: bold;
    padding-bottom: 5px;
    border-bottom: 1px solid #e8e8e8;
  }
  .config-body {
    max-height: 500px;
    overflow-y: auto;
  }
  .config-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    cursor: move;
    &:hover {
      background-color: #f5f7fa;
    }
  }
  .config-col-name {
    width: 140px;
    display: flex;
    align-items: center;
    i {
      margin-right: 5px;
      color: #c0c4cc;
    }
  }
  .config-col-visible,
  .config-col-sortable,
  .config-col-tooltip {
    width: 180px;
    display: flex;
    justify-content: center;
  }
  .config-col-fixed {
    width: 180px;
    display: flex;
    justify-content: center;
    ::v-deep .el-radio {
      margin-right: 8px;
    }
    ::v-deep .el-radio__label {
      padding-left: 4px;
    }
  }
  .config-empty {
    text-align: center;
    color: #909399;
    padding: 20px 0;
  }
</style>
